import { Button as HeadlessButton, type ButtonProps as HeadlessButtonProps } from '@headlessui/react';
import { clsx } from 'clsx';
import { type ComponentPropsWithoutRef, type ReactNode, type ForwardedRef } from 'react';
import { forwardRef } from 'react';
import TouchTarget from './TouchTarget';
import Link from '@/components/common/Link';

const base = [
    // Base
    'relative isolate inline-flex items-center justify-center gap-x-2 rounded-lg text-base font-semibold',
    // Sizing
    'px-[calc(--spacing(3.5)-1px)] py-1.5 sm:h-[38px] sm:px-[calc(--spacing(3)-1px)] sm:py-0',
    // Focus
    'data-focus:outline data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-teal-500',
    // Disabled
    'data-disabled:opacity-50 data-disabled:cursor-not-allowed',
    // cursors
    'cursor-pointer disabled:cursor-not-allowed',
    // Icon
    '*:data-[slot=icon]:-mx-0.5 *:data-[slot=icon]:my-0.5 *:data-[slot=icon]:size-5 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:self-center *:data-[slot=icon]:text-(--btn-icon) sm:*:data-[slot=icon]:my-1 sm:*:data-[slot=icon]:size-4 forced-colors:[--btn-icon:ButtonText] forced-colors:data-hover:[--btn-icon:ButtonText]',
];

const styles = {
    base: [
        // Base
        'relative isolate inline-flex items-center justify-center gap-x-2 rounded-lg text-base font-semibold',
        // Sizing
        'px-[calc(--spacing(3.5)-1px)] py-1.5 sm:h-[38px] sm:px-[calc(--spacing(3)-1px)] sm:py-0',
        // Focus
        'data-focus:outline data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-teal-500',
        // Disabled
        'data-disabled:opacity-50',
        // Icon
        '*:data-[slot=icon]:-mx-0.5 *:data-[slot=icon]:my-0.5 *:data-[slot=icon]:size-5 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:self-center *:data-[slot=icon]:text-(--btn-icon) sm:*:data-[slot=icon]:my-1 sm:*:data-[slot=icon]:size-4 forced-colors:[--btn-icon:ButtonText] forced-colors:data-hover:[--btn-icon:ButtonText]',
    ],
    solid: [
        // Optical border, implemented as the button background to avoid corner artifacts
        'border-transparent bg-(--btn-border)',
        // Dark mode: border is rendered on `after` so background is set to button background
        'dark:bg-(--btn-bg)',
        // Button background, implemented as foreground layer to stack on top of pseudo-border layer
        'before:absolute before:inset-0 before:-z-10 before:rounded-[calc(var(--radius-lg)-1px)] before:bg-(--btn-bg)',
        // Drop shadow, applied to the inset `before` layer so it blends with the border
        'before:shadow-none',
        // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
        'dark:before:hidden',
        // Dark mode: Subtle white outline is applied using a border
        'dark:border-white/5',
        // Shim/overlay, inset to match button foreground and used for hover state + highlight shadow
        'after:absolute after:inset-0 after:-z-10 after:rounded-[calc(var(--radius-lg)-1px)]',
        // Inner highlight shadow
        'after:shadow-[shadow:inset_0_1px_--theme(--color-white/15%)]',
        // White overlay on hover
        'data-active:after:bg-(--btn-hover-overlay) data-hover:after:bg-(--btn-hover-overlay)',
        // Dark mode: `after` layer expands to cover entire button
        'dark:after:-inset-px dark:after:rounded-lg',
        // Disabled
        'data-disabled:before:shadow-none data-disabled:after:shadow-none data-disabled:before:bg-neutral-400 data-disabled:text-neutral-700',
    ],
    outline: [
        // Base
        'outline outline-1 outline-neutral-300 text-neutral-800 font-semibold data-active:bg-zinc-950/[2.5%] data-hover:bg-zinc-950/[2.5%]',
        // Dark mode
        'dark:outline-white/15 dark:text-white dark:[--btn-bg:transparent] dark:data-active:bg-white/5 dark:data-hover:bg-white/5',
        // Icon
        '[--btn-icon:var(--color-zinc-500)] data-active:[--btn-icon:var(--color-zinc-700)] data-hover:[--btn-icon:var(--color-zinc-700)] dark:data-active:[--btn-icon:var(--color-zinc-400)] dark:data-hover:[--btn-icon:var(--color-zinc-400)]',
    ],
    plain: [
        // Base
        'border-transparent text-zinc-950 data-active:bg-zinc-950/5 data-hover:bg-zinc-950/5',
        // Dark mode
        'dark:text-white dark:data-active:bg-white/10 dark:data-hover:bg-white/10',
        // Icon
        '[--btn-icon:var(--color-zinc-500)] data-active:[--btn-icon:var(--color-zinc-700)] data-hover:[--btn-icon:var(--color-zinc-700)] dark:[--btn-icon:var(--color-zinc-500)] dark:data-active:[--btn-icon:var(--color-zinc-400)] dark:data-hover:[--btn-icon:var(--color-zinc-400)]',
    ],
    text: [
        // Base styling - transparent background, no border
        'border-transparent bg-transparent',
        // Dark mode
        'dark:text-red-400 dark:data-hover:bg-red-950/20 dark:data-active:bg-red-950/30',
        // Icon
        '[--btn-icon:var(--color-red-600)] data-hover:[--btn-icon:var(--color-red-700)] data-active:[--btn-icon:var(--color-red-700)]',
        'dark:[--btn-icon:var(--color-red-400)] dark:data-hover:[--btn-icon:var(--color-red-300)] dark:data-active:[--btn-icon:var(--color-red-300)]',
    ],
};

const colors = {
    default: [
        'text-white [--btn-bg:var(--color-zinc-900)] [--btn-border:var(--color-zinc-950)]/90 [--btn-hover-overlay:var(--color-white)]/10',
        'dark:text-white dark:[--btn-bg:var(--color-zinc-600)] dark:[--btn-hover-overlay:var(--color-white)]/5',
        '[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-300)] data-hover:[--btn-icon:var(--color-zinc-300)]',
    ],
    primary: [
        'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-pink-500)] [--btn-border:var(--color-pink-600)]/90',
        '[--btn-icon:var(--color-pink-300)] data-active:[--btn-icon:var(--color-pink-200)] data-hover:[--btn-icon:var(--color-pink-200)]',
    ],
    teal: [
        'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-teal-600)]',
        '[--btn-icon:var(--color-teal-300)] data-active:[--btn-icon:var(--color-teal-200)] data-hover:[--btn-icon:var(--color-teal-500)]',
    ],
    secondary: [
        'text-gray-700 [--btn-hover-overlay:var(--color-white-400)]/10 [--btn-bg:var(--color-white-100)] [--btn-border:var(--color-white-300)]/90',
        '[--btn-icon:var(--color-white-500)] data-active:[--btn-icon:var(--color-white-600)] data-hover:[--btn-icon:var(--color-white-600)]',
    ],
    disabled: [
        'text-neutral-700 [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-neutral-400)] [--btn-border:var(--color-neutral-400)]/90',
        '[--btn-icon:var(--color-neutral-700)] data-active:[--btn-icon:var(--color-neutral-700)] data-hover:[--btn-icon:var(--color-neutral-700)]',
    ],
    danger: [
        'text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-red-600)] [--btn-border:var(--color-red-700)]/90',
        '[--btn-icon:var(--color-red-300)] data-active:[--btn-icon:var(--color-red-200)] data-hover:[--btn-icon:var(--color-red-200)]',
    ],
    delete: [
        'text-red-500',
        'data-hover:bg-red-50 data-active:bg-red-100',
        'data-disabled:text-red-300 dark:data-disabled:text-red-600',
    ],
    link: [
        'text-blue-600',
        'hover:underline focus:underline active:underline',
        'bg-transparent border-none shadow-none',
        'data-hover:bg-transparent data-active:bg-transparent',
        'data-disabled:text-blue-300 dark:data-disabled:text-blue-600',
    ],
    lightTeal: ['text-teal-600 bg-teal-50'],
};

const outlineColors: Partial<Record<keyof typeof colors, string[]>> = {
    default: ['bg-white'],
    teal: [
        'outline-teal-200 bg-teal-50 text-teal-600 data-hover:bg-teal-100 data-active:bg-teal-100',
        '[--btn-icon:var(--color-teal-500)] data-hover:[--btn-icon:var(--color-teal-600)] data-active:[--btn-icon:var(--color-teal-600)]',
    ],
};

export type ButtonProps = {
    color?: keyof typeof colors;
    outline?: boolean;
    plain?: boolean;
    text?: boolean;
    className?: string;
    children: ReactNode;
    alignStart?: boolean;
    ignoreVariantStyle?: boolean;
    compact?: boolean; // For modal buttons with tighter spacing
} & (Omit<HeadlessButtonProps, 'as' | 'className'> | Omit<ComponentPropsWithoutRef<typeof Link>, 'className'>);

const Button = forwardRef(function Button(
    {
        color,
        outline,
        plain,
        text,
        className,
        children,
        ignoreVariantStyle,
        alignStart,
        compact,
        ...props
    }: ButtonProps,
    ref: ForwardedRef<HTMLElement>
) {
    // Determine button variant styles based on props
    const variantStyles = text
        ? clsx(styles.text, colors[color ?? 'default'])
        : outline
          ? clsx(styles.outline, outlineColors[color ?? 'default'])
          : plain
            ? styles.plain
            : clsx(styles.solid, colors[color ?? 'default']);

    // Compact styles for modal buttons
    const compactStyles = compact
        ? 'rounded-md px-2.5 py-1.5 !h-[38px] !items-center text-base leading-6 font-semibold shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]'
        : '';

    // Additional outline styles for compact variant
    const compactOutlineStyles =
        compact && outline ? 'outline outline-1 outline-offset-[-1px] outline-neutral-300' : '';

    const classes = ignoreVariantStyle
        ? clsx(className, base, alignStart && 'justify-start', compactStyles, compactOutlineStyles)
        : clsx(className, base, variantStyles, alignStart && 'justify-start', compactStyles, compactOutlineStyles);

    return 'to' in props ? (
        <Link {...props} className={classes} ref={ref as ForwardedRef<HTMLAnchorElement>}>
            <TouchTarget>{children}</TouchTarget>
        </Link>
    ) : (
        <HeadlessButton type="button" {...props} className={clsx(classes, 'cursor-default')} ref={ref}>
            <TouchTarget>{children}</TouchTarget>
        </HeadlessButton>
    );
});

export default Button;
